import { Component, OnInit, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators, FormControl } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { InventoryService } from '../../services/inventory.service';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { PrintService } from '../../services/print.service';
import * as XLSX from 'xlsx';

@Component({
	selector: 'app-forced-cost-override',
	templateUrl: './forced-cost-override.component.html',
	styleUrls: ['./forced-cost-override.component.scss']
})
export class ForcedCostOverrideComponent implements OnInit {

	@ViewChild('costOverrideEdit') costOverrideEditRef: ElementRef;
	@ViewChild('costOverrideUpload') costOverrideUploadRef: ElementRef;
	@ViewChild('forcedCostOverridePaginator') forcedCostOverridePaginator: MatPaginator;
	@ViewChild('forcedCostOverrideSort') forcedCostOverrideSort: MatSort;

	forcedCostOverrideForm: UntypedFormGroup;
	forcedCostOverrides: any[] = [];
	costOverrideFiles: any[] = [];
	costOverride_processing_recs = false;
	costOverrideReviewReady = false;
	costOverrideHelp = false;
	costOverrideUploadedHeaders: string[] = [];
	costOverrideUploadedRawData: any[] = [];
	costOverrideColumnMappings: any = {};
	costOverrideHasDuplicateMappings = false;
	costOverrideFileSpecificHeaders: {
		[fileName: string]: string[]
	} = {};
	costOverrideFileSpecificMappings: {
		[fileName: string]: {
			[header: string]: string
		}
	} = {};
	costOverrideSelectedFile = 'all';
	costOverrideAvailableFiles: string[] = [];
	costOverrideAvailableFieldTypes: string[] = ['stockid', 'brand', 'partno', 'cost', 'ignore'];
	costOverrideDataSource: any[] = [];
	stockIdMappingInProgress = false;
	stockIdMappingProgress = 0;
	unmappedRows = 0;
	costOverrideReadyForSave = false;
	forcedCostOverrideDisplayedColumns: string[] = ['stockid', 'description', 'override_cost', 'actions'];
	forcedCostOverrideDataSource: MatTableDataSource < any > = new MatTableDataSource < any > ([]);
	overidesLoading = false;

	items_per_page = [25, 50, 100, 500];
	pagesizedefault = 25;
	missingSKUs: any = [];

	public isSearching = false;
	public currentBatch = 0;
	public totalBatches = 0;
	public totalProcessed = 0;

	constructor(
		private printService: PrintService,
		private _changeDetectorRef: ChangeDetectorRef,
		private fb: UntypedFormBuilder,
		private inventoryService: InventoryService,
		private globalSearchService: GlobalSearchService,
		private modalService: NgbModal
	) {}

	ngOnInit(): void {
		this.loadForcedCostOverrides();
	}

	loadForcedCostOverrides() {
		this.overidesLoading = true;
		this.inventoryService.getforcedcostoverride().subscribe((data: any[]) => {
			this.forcedCostOverrides = data;
			this.forcedCostOverrideDataSource = new MatTableDataSource < any > (this.forcedCostOverrides);
			setTimeout(() => {
				this.forcedCostOverrideDataSource.paginator = this.forcedCostOverridePaginator;
				this.forcedCostOverridePaginator.pageIndex = 0;
				this.forcedCostOverridePaginator.pageSize = this.pagesizedefault;
				this.forcedCostOverridePaginator.length = this.forcedCostOverrides.length;
				this.forcedCostOverridePaginator.page.next({
					pageIndex: 0,
					pageSize: this.pagesizedefault,
					length: this.forcedCostOverrides.length
				});
				if (this.forcedCostOverrideSort) {
					this.forcedCostOverrideDataSource.sort = this.forcedCostOverrideSort;
				}
				this.overidesLoading = false;
				this._changeDetectorRef.detectChanges();
			}, 300);
		});
	}

	exportForcedCostOverridePDF() {
		const encoded: string = this.globalSearchService.base64encode(this.generateForcedCostOverrideHtml());
		const today = new Date();
		const data = {
			content: encoded,
			filename: 'ForcedCostOverrides.pdf',
			title: 'Forced Cost Overrides',
			subtitle: ' created ' + today.toLocaleString()
		};
		this.printService.pdf(data).subscribe((result: any) => {
			this.globalSearchService.downloadPdf(result.content, data.filename);
		});
	}

	exportForcedCostOverrideExcel() {
		const encoded: string = this.globalSearchService.base64encode(this.generateForcedCostOverrideHtml());
		const today = new Date();
		const data = {
			content: encoded,
			filename: 'ForcedCostOverrides.xlsx'
		};
		this.printService.xls(data).subscribe((result: any) => {
			this.globalSearchService.downloadXls(result.content, data.filename);
		});
	}

	editForcedCostOverride(item: any) {
		this.forcedCostOverrideForm = this.fb.group({
			stockid: [item.stockid, Validators.required],
			override_cost: [item.override_cost || item.cost, Validators.required]
		});
		this.forcedCostOverrideForm.get('stockid').disable();
		this.modalService.open(this.costOverrideEditRef, { ariaLabelledBy: 'modal-title', size: 'lg' });
	}

	removeForcedCostOverride(item: any) {
		if (confirm(`Delete cost override for ${item.stockid}`)) {
			this.inventoryService.removeForcedCostOverride(item).subscribe(() => {
				this.loadForcedCostOverrides();
			});
		}
	}

	applyFilterCostOverride(event: Event) {
		const filterValue = (event.target as HTMLInputElement).value;
		this.forcedCostOverrideDataSource.filter = filterValue.trim().toLowerCase();
		if (this.forcedCostOverrideDataSource.paginator) {
			this.forcedCostOverrideDataSource.paginator.firstPage();
		}
	}

	addForcedCostOverride() {
		this.forcedCostOverrideForm = this.fb.group({
			stockid: ['', Validators.required],
			override_cost: [0, Validators.required]
		});
		this.modalService.open(this.costOverrideEditRef, { ariaLabelledBy: 'modal-title', size: 'lg' });
	}

	uploadForcedCostOverride() {
		this.resetCostOverrideUpload();
		this.modalService.open(this.costOverrideUploadRef, { ariaLabelledBy: 'modal-title', size: 'xl' }).result.then(() => {}, () => {
			this.resetCostOverrideUpload();
		});
	}

	toggleCostOverrideHelp() {
		this.costOverrideHelp = !this.costOverrideHelp;
	}

	onCostOverrideSelect(event: any) {
		this.costOverrideFiles.push(...event.addedFiles);
		if (!this.costOverride_processing_recs) {
			this.processCostOverride();
		}
	}

	onCostOverrideRemove(event: any) {
		this.costOverrideFiles.splice(this.costOverrideFiles.indexOf(event), 1);
		if (this.costOverrideFiles.length > 0) {
			if (!this.costOverride_processing_recs) {
				this.processCostOverride();
			}
		} else {
			this.resetCostOverrideUpload();
		}
	}

	resetCostOverrideUpload() {
		this.costOverrideReviewReady = false;
		this.costOverride_processing_recs = false;
		this.costOverrideUploadedRawData = [];
		this.costOverrideUploadedHeaders = [];
		this.costOverrideColumnMappings = {};
		this.costOverrideFileSpecificHeaders = {};
		this.costOverrideFileSpecificMappings = {};
		this.costOverrideAvailableFiles = [];
		this.costOverrideSelectedFile = 'all';
		this.costOverrideDataSource = [];
		this.costOverrideFiles = [];
	}



	async processCostOverride() {
		if (this.costOverrideFiles.length === 0) {return;}

		this.costOverrideUploadedRawData = [];
		this.costOverrideUploadedHeaders = [];
		this.costOverrideFileSpecificHeaders = {};
		this.costOverrideFileSpecificMappings = {};
		this.costOverrideAvailableFiles = [];
		this.costOverride_processing_recs = true;

		let filesProcessed = 0;
		let allFileData: any[] = [];

		for (let i = 0; i < this.costOverrideFiles.length; i++) {
			const file = this.costOverrideFiles[i];
			const name = file.name;
			const type = file.type;

			if (!this.costOverrideAvailableFiles.includes(name)) {
				this.costOverrideAvailableFiles.push(name);
			}

			try {
				const fileContents = await this.readFile(file);
				const workbook = XLSX.read(fileContents, { type: 'binary' });
				console.log(workbook);
				const sheetName = workbook.SheetNames[0];
				const worksheet = workbook.Sheets[sheetName];
				const rows: Record < string, any > [] = XLSX.utils.sheet_to_json(worksheet, { defval: '' });


				const dataWithFileSource = rows.map(row => ({ ...row, _sourceFile: name }));
				allFileData = [...allFileData, ...dataWithFileSource];

				console.log(allFileData);

				if (rows && rows.length > 0) {
					const currentHeaders = Object.keys(rows[0]);
					this.costOverrideFileSpecificHeaders[name] = [...currentHeaders];
					this.costOverrideUploadedHeaders = [...new Set([...this.costOverrideUploadedHeaders, ...currentHeaders])];
				}

				filesProcessed++;
			} catch (error) {
				filesProcessed++;
				console.error('Error processing file:', error);
				this.globalSearchService.showNotification('Error processing file: ' + name, 'danger', 'bottom', 'left');
			}

			if (filesProcessed === this.costOverrideFiles.length) {
				this.costOverrideUploadedRawData = allFileData;
				this.costOverrideSelectedFile = 'all';
				this.initializeCostOverrideColumnMappings();
				this.costOverrideReviewReady = true;
				this.costOverride_processing_recs = false;
			}
		}
	}


	initializeForcedCostOverridePaginator() {
		if (this.forcedCostOverrides?.length > 0 && this.forcedCostOverridePaginator) {
			this.forcedCostOverrideDataSource = new MatTableDataSource < any > (this.forcedCostOverrides);
			this.forcedCostOverrideDataSource.paginator = this.forcedCostOverridePaginator;
			if (this.forcedCostOverrideSort) {
				this.forcedCostOverrideDataSource.sort = this.forcedCostOverrideSort;
			}
			this._changeDetectorRef.detectChanges();
		}
	}

	initializeCostOverrideColumnMappings() {
		this.costOverrideColumnMappings = {};
		this.costOverrideUploadedHeaders = [];
		this.costOverrideHasDuplicateMappings = false;

		// Step 1: Ensure headers exist per file and generate file-specific mappings
		this.costOverrideAvailableFiles.forEach(fileName => {
			if (!this.costOverrideFileSpecificHeaders[fileName]) {
				const rows = this.costOverrideUploadedRawData.filter(row => row._sourceFile === fileName);
				if (rows.length > 0) {
					this.costOverrideFileSpecificHeaders[fileName] = Object.keys(rows[0]).filter(key => key !== '_sourceFile');
				} else {
					this.costOverrideFileSpecificHeaders[fileName] = [];
				}
			}

			const headers = this.costOverrideFileSpecificHeaders[fileName] || [];
			this.costOverrideUploadedHeaders.push(...headers);

			if (!this.costOverrideFileSpecificMappings[fileName]) {
				this.costOverrideFileSpecificMappings[fileName] = {};
			}

			const rules = [
				{ keywords: ['stock', 'sku', 'internal', 'item'], mapTo: 'stockid' },
				{ keywords: ['brand', 'make'], mapTo: 'brand' },
				{ keywords: ['part no', 'part#', 'part_number'], mapTo: 'partno' },
				{ keywords: ['cost', 'price', 'value', 'override'], mapTo: 'cost' }
			];

			const alreadyUsedFields = new Set < string > ();

			headers.forEach(header => {
				const h = header.toLowerCase();
				let mapped = 'ignore';

				for (const rule of rules) {
					if (rule.keywords.some(k => h.includes(k)) && !alreadyUsedFields.has(rule.mapTo)) {
						mapped = rule.mapTo;
						alreadyUsedFields.add(rule.mapTo);
						break;
					}
				}
				this.costOverrideFileSpecificMappings[fileName][header] = mapped;
			});
		});

		// Step 2: De-duplicate all headers
		this.costOverrideUploadedHeaders = [...new Set(this.costOverrideUploadedHeaders)];

		// Step 3: Build consistent overall column mapping if all files agree
		this.costOverrideUploadedHeaders.forEach(header => {
			let firstMapping: string | null = null;
			let consistent = true;

			for (const fileName of this.costOverrideAvailableFiles) {
				const headers = this.costOverrideFileSpecificHeaders[fileName];
				if (headers && headers.includes(header)) {
					const current = this.costOverrideFileSpecificMappings[fileName][header];
					if (firstMapping === null) {
						firstMapping = current;
					} else if (current !== firstMapping) {
						consistent = false;
						break;
					}
				}
			}

			this.costOverrideColumnMappings[header] = consistent ? firstMapping : 'ignore';
		});

		// Final duplicate mapping check
		this.costOverrideHasDuplicateMappings = this.checkForCostOverrideDuplicateMappings();
	}


	checkForCostOverrideDuplicateMappings(): boolean {
		const fileName = this.costOverrideSelectedFile;
		if (fileName === 'all') {
			const fieldTypeCounts: Record < string, number > = {};
			for (const [, fieldType] of Object.entries(this.costOverrideColumnMappings)) {
				if (fieldType === 'ignore') {continue;}
				const key = fieldType as string;
				fieldTypeCounts[key] = (fieldTypeCounts[key] || 0) + 1;
				if (fieldTypeCounts[key] > 1) {
					return true;
				}
			}
			return false;
		} else {
			if (!this.costOverrideFileSpecificMappings[fileName]) {
				return false;
			}
			const fieldTypeCounts: Record < string, number > = {};
			for (const [, fieldType] of Object.entries(this.costOverrideFileSpecificMappings[fileName])) {
				if (fieldType === 'ignore') {continue;}
				const key = fieldType as string;
				fieldTypeCounts[key] = (fieldTypeCounts[key] || 0) + 1;
				if (fieldTypeCounts[key] > 1) {
					return true;
				}
			}
			return false;
		}
	}

	async searchMissingSKUs() {
		this.isSearching = true;
		this.currentBatch = 0;
		this.totalProcessed = 0;

		const mappingSource = this.costOverrideSelectedFile === 'all' ?
			this.costOverrideColumnMappings :
			this.costOverrideFileSpecificMappings[this.costOverrideSelectedFile];

		const lookupFields = Object.keys(mappingSource).filter(k => mappingSource[k] !== 'ignore');

		if (!lookupFields.length) {
			this.globalSearchService.showNotification('No valid fields mapped for lookup', 'warning', 'bottom', 'left');
			this.isSearching = false;
			return;
		}

		const relevantRows = this.costOverrideUploadedRawData.filter(row =>
			this.costOverrideSelectedFile === 'all' || row._sourceFile === this.costOverrideSelectedFile
		);

		const searchPayloads = relevantRows.map(row => {
			const payload: Record < string, any > = {};
			for (const key of lookupFields) {
				payload[mappingSource[key]] = row[key];
			}
			payload._originalRow = row;
			return payload;
		}).filter(p => Object.values(p).some(v => typeof v === 'string' && !!v));

		const batchSize = 100;
		let totalMissing = 0;
		this.totalBatches = Math.ceil(searchPayloads.length / batchSize);

		for (let i = 0; i < searchPayloads.length; i += batchSize) {
			this.currentBatch = Math.floor(i / batchSize);
			const batch = searchPayloads.slice(i, i + batchSize);
			this.totalProcessed += batch.length;

			try {
				const res = await this.inventoryService.lookupMissingSkus(batch).toPromise();
				const found = res?.results || [];

				const safeLower = (val: any): string =>
					typeof val === 'string' ? val.toLowerCase() : String(val || '').toLowerCase();

				const foundMap = new Map < string,
					string > ();
				for (const f of found) {
					const key = `${safeLower(f.brand)}||${safeLower(f.partno)}`;
					foundMap.set(key, f.stockid || '');
				}

				for (const item of batch) {
					const row = item._originalRow;
					const key = `${safeLower(item.brand)}||${safeLower(item.partno)}`;
					const resolvedStockId = foundMap.get(key);
					if (resolvedStockId) {
						row.stockid = resolvedStockId;
						row._stockid_missing = false;
					} else {
						row.stockid = row.stockid || '';
						row._stockid_missing = !row.stockid;
						if (row._stockid_missing) {totalMissing++;}
					}
				}

				this.costOverrideUploadedRawData = [...this.costOverrideUploadedRawData];


			} catch (err) {
				console.error(`Batch ${this.currentBatch + 1} failed:`, err);
				continue;
			}

		}

		// Refresh binding to trigger change detection
		this.costOverrideUploadedRawData = [...this.costOverrideUploadedRawData];

		// Update headers
		const headerList = this.costOverrideSelectedFile === 'all' ?
			this.costOverrideUploadedHeaders :
			this.costOverrideFileSpecificHeaders[this.costOverrideSelectedFile];

		if (Array.isArray(headerList) && !headerList.includes('stockid')) {
			headerList.unshift('stockid');
		}

		// Automatically map 'stockid' header to 'stockid' field if it's not mapped yet
		if (this.costOverrideSelectedFile === 'all') {
			if (!this.costOverrideColumnMappings['stockid']) {
				this.costOverrideColumnMappings['stockid'] = 'stockid';
				this.costOverrideAvailableFieldTypes = Array.from(new Set([...this.costOverrideAvailableFieldTypes, 'stockid']));
			}
			for (const file of this.costOverrideAvailableFiles) {
				if (!this.costOverrideFileSpecificMappings[file]['stockid']) {
					this.costOverrideFileSpecificMappings[file]['stockid'] = 'stockid';
				}
			}
		} else {
			const file = this.costOverrideSelectedFile;
			if (!this.costOverrideFileSpecificMappings[file]['stockid']) {
				this.costOverrideFileSpecificMappings[file]['stockid'] = 'stockid';
			}
		}


		this.isSearching = false;

		this.globalSearchService.showNotification(
			totalMissing ? `Marked ${totalMissing} rows as missing` : 'All rows resolved',
			totalMissing ? 'warning' : 'success',
			'bottom',
			'left'
		);
	}

	isFieldTypeAlreadyUsed(type: string, currentHeader: string): boolean {
		if (type === 'ignore') {return false;} // allow multiple 'ignore'

		const mappingSource = this.costOverrideSelectedFile === 'all' ?
			this.costOverrideColumnMappings :
			this.costOverrideFileSpecificMappings[this.costOverrideSelectedFile] || {};

		// Count how many times this type is already selected
		const usedCount = Object.entries(mappingSource).filter(
			([header, mappedType]) => mappedType === type && header !== currentHeader
		).length;

		return usedCount > 0;
	}


	// Shows only matched original rows
	get matchedOriginalRows(): any[] {
		return this.costOverrideUploadedRawData.filter(row =>
			(this.costOverrideSelectedFile === 'all' || row._sourceFile === this.costOverrideSelectedFile) &&
			!!row.stockid &&
			row._stockid_missing === false
		);
	}


	// Shows only mapped field keys like: stockid, brand, partno, cost
	get mappedReviewFields(): string[] {
		const mapping = this.costOverrideSelectedFile === 'all' ?
			this.costOverrideColumnMappings :
			this.costOverrideFileSpecificMappings[this.costOverrideSelectedFile] || {};

		const usedFields = new Set < string > ();

		for (const type of this.costOverrideAvailableFieldTypes) {
			if (type === 'ignore') {continue;}
			if (Object.values(mapping).includes(type)) {
				usedFields.add(type);
			}
		}

		return Array.from(usedFields);
	}

	getMappedOriginalField(mappedName: string): string | undefined {
		const mapping = this.costOverrideSelectedFile === 'all'
			? this.costOverrideColumnMappings
			: this.costOverrideFileSpecificMappings[this.costOverrideSelectedFile] || {};

		return Object.entries(mapping)
			.find(([_, mapped]) => mapped === mappedName)?.[0];
	}


	get mappedReviewFieldPairs(): { original: string;mapped: string } [] {
		const mapping = this.costOverrideSelectedFile === 'all' ?
			this.costOverrideColumnMappings :
			this.costOverrideFileSpecificMappings[this.costOverrideSelectedFile] || {};

		const seen = new Set < string > ();
		const pairs: { original: string;mapped: string } [] = [];

		for (const [original, mapped] of Object.entries(mapping) as[string, string][]) {
			if (mapped && mapped !== 'ignore' && !seen.has(mapped)) {
				seen.add(mapped);
				pairs.push({ original, mapped });
			}
		}

		return pairs;
	}


	get mappedFieldPairs(): { original: string;mapped: string } [] {
		const mapping = this.costOverrideSelectedFile === 'all' ?
			this.costOverrideColumnMappings :
			this.costOverrideFileSpecificMappings[this.costOverrideSelectedFile] || {};

		const seen = new Set < string > ();
		const result: { original: string;mapped: string } [] = [];

		for (const [original, mapped] of Object.entries(mapping) as[string, string][]) {
			if (mapped && mapped !== 'ignore' && !seen.has(mapped)) {
				result.push({ original, mapped });
				seen.add(mapped);
			}
		}

		return result;
	}

	get unmatchedItems(): any[] {
		return this.costOverrideUploadedRawData?.filter(i => !i.stockid) || [];
	}

	get matchedCount(): number {
		return this.costOverrideUploadedRawData?.filter(i => !!i.stockid).length || 0;
	}

	get unmatchedCount(): number {
		return this.costOverrideUploadedRawData?.filter(i => !i.stockid).length || 0;
	}

	get matchedItems(): any[] {
		return this.costOverrideUploadedRawData?.filter(i => !!i.stockid) || [];
	}

	get hasStockIds(): boolean {
		return this.costOverrideUploadedRawData?.some(i => !!i.stockid);
	}

	private hasAnyMapping(field: string): boolean {
		if (this.costOverrideSelectedFile === 'all') {
			return Object.values(this.costOverrideColumnMappings).includes(field);
		} else {
			const mappings = this.costOverrideFileSpecificMappings[this.costOverrideSelectedFile] || {};
			return Object.values(mappings).includes(field);
		}
	}

	isBrandAndPartnoMapped(): boolean {
		const mapping = this.costOverrideSelectedFile === 'all'
			? this.costOverrideColumnMappings
			: this.costOverrideFileSpecificMappings[this.costOverrideSelectedFile] || {};

		const mappedFields = Object.values(mapping);
		return mappedFields.includes('brand') && mappedFields.includes('partno');
	}


	readFile(file: File): Promise < string > {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = () => resolve(reader.result as string);
			reader.onerror = reject;
			reader.readAsBinaryString(file);
		});
	}

	processMatchedRows(): void {
		if (!this.matchedItems.length) {
			this.globalSearchService.showNotification('No matched rows to process.', 'warning', 'bottom', 'left');
			return;
		}

		this.costOverride_processing_recs = true;

		const fieldMap = this.mappedFieldPairs;

		const payload = this.matchedItems.map(item => {
			const mapped: any = {};
			for (const pair of fieldMap) {
				if (pair.mapped === 'stockid') {
					mapped.stockid = item[pair.original];
				}
				if (pair.mapped === 'cost') {
					mapped.override_cost = parseFloat(item[pair.original] || 0);
				}
				// add more mappings as needed
			}
			return mapped;
		});

		this.inventoryService.saveForcedCostOverrideBatch(payload).subscribe(
			(result: any) => {
				if (result && result.success) {
					this.globalSearchService.showNotification(`Successfully updated ${result.updated} cost overrides`, 'success', 'bottom', 'left');
					this.loadForcedCostOverrides();
					this.resetCostOverrideUpload();
					this.modalService.dismissAll();
				} else {
					this.globalSearchService.showNotification('Error saving overrides: ' + (result?.message || 'Unknown error'), 'danger', 'bottom', 'left');
				}
				this.costOverride_processing_recs = false;
			},
			(error) => {
				console.error('Save error', error);
				this.globalSearchService.showNotification('Error saving matched overrides', 'danger', 'bottom', 'left');
				this.costOverride_processing_recs = false;
			}
		);
	}

	generateForcedCostOverrideHtml(): string {
		let html = '<table>';
		html += '<tr>';
		html += '<td>Item#</td>';
		html += '<td>Description</td>';
		html += '<td>Override Cost</td>';
		html += '</tr>';
		this.forcedCostOverrides.forEach(item => {
			html += '<tr>';
			html += `<td>${item.stockid || ''}</td>`;
			html += `<td>${item.description || ''}</td>`;
			html += `<td>${item.override_cost || '0.00'}</td>`;
			html += '</tr>';
		});
		html += '</table>';
		return html;
	}

	getCurrentMapping(header: string): string {
		const mappingSource = this.costOverrideSelectedFile === 'all' ?
			this.costOverrideColumnMappings :
			this.costOverrideFileSpecificMappings[this.costOverrideSelectedFile] || {};

		return mappingSource[header] || 'ignore';
	}

	updateCostOverrideColumnMapping(header: string, newType: string, fileName: string | null = null) {
		if (fileName) {
			this.costOverrideFileSpecificMappings[fileName][header] = newType;
		} else {
			this.costOverrideColumnMappings[header] = newType;
		}

		// Recheck for duplicate mappings
		this.costOverrideHasDuplicateMappings = this.checkForCostOverrideDuplicateMappings();

		// Force Angular to re-evaluate template
		this._changeDetectorRef.detectChanges();
	}

	saveForcedCostOverride() {
		if (this.forcedCostOverrideForm.valid) {
			if (this.forcedCostOverrideForm.get('stockid').disabled) {
				this.forcedCostOverrideForm.get('stockid').enable();
			}
			const data = {
				stockid: this.forcedCostOverrideForm.get('stockid').value,
				override_cost: this.forcedCostOverrideForm.get('override_cost').value
			};
			this.inventoryService.saveForcedCostOverride(data).subscribe(
				(result: any) => {
					if (result && result.success) {
						this.loadForcedCostOverrides();
						this.modalService.dismissAll();
						this.globalSearchService.showNotification(result.message || 'Cost override saved successfully', 'success', 'bottom', 'left');
					} else {
						this.globalSearchService.showNotification(result.message || 'Error saving cost override', 'danger', 'bottom', 'left');
					}
				},
				() => {
					this.globalSearchService.showNotification('Error saving cost override', 'danger', 'bottom', 'left');
				}
			);
		}
	}
}